import { t } from '@hefe/i18n';
import {
  BasketballEvent,
  BasketballEventDetails,
  BasketballEventStatus,
  BasketballEventType,
  BasketballGame,
  BasketballMessage,
  BasketballMessagesPagination,
  BasketballPlayer,
  BasketballTeamJudgeGame,
  BasketballTeamJudgeGameAutoAssignment,
  BasketballTeamTask,
  BasketballTeamTaskEvent,
  BasketballTeamTaskEventAssignment,
  RequestBasketballPostEvent,
  RequestBasketballPostTeamJudgesUsersAutoAssignment,
  ResponseBasketballUpdateJudgeUser,
  SelectOption,
  Task,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import API from '../../../api/api';
import { isCacheValid } from '../../../utils/cache/caching';
import { eventsApi } from '../../base/events/events.api';
import {
  createBasketballTeamTask,
  deleteTask,
  fetchBasketballTeamTasks,
  updateTask,
} from '../../base/tasks/tasks.actions';
import { UserState } from '../../base/user/user.slice';
import {
  deleteBasketballGame,
  saveBasketballGame,
} from '../actions/basketball-game/basketball-game.actions';
import { assignUsersToBasketballEventTask } from '../basketball-event-tasks/basketball-event-tasks.slice';
import { setBasketballGameJudgeUser } from '../basketball-games/basketball-games.slice';
import {
  createBasketballMessage,
  deleteBasketballMessage,
} from '../basketball-messages/basketball-messages.slice';
import {
  deleteBasketballPractices,
  linkBasketballPractices,
  saveBasketballPractice,
  saveBasketballPractices,
  updateBasketballPractices,
} from '../basketball-practices/basketball-practices.slice';
import {
  addPlayersToBasketballTeam,
  removePlayersFromBasketballTeam,
  updateBasketballPlayer,
} from '../basketball-teams/basketball-teams.slice';

const FEATURE_KEY = 'basketballTeam';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchBasketballTeamEvents = createAsyncThunk(
  'basketballTeam/getAllEvents',
  async (teamId: number, thunkAPI) => {
    const response = await API.basketballTeams().getEvents(teamId);
    return response?.data;
  }
);

/** @deprecated since version 1.0 */
export const fetchBasketballTeamGames = createAsyncThunk(
  'basketballTeam/getAllGAmes',
  async (_, thunkAPI) => {
    const response = await API.basketballMyTeam().getAllGames();
    return response?.data;
  }
);

export const fetchBasketballTeamJudgeGames = createAsyncThunk(
  'basketballTeam/getJudgeGames',
  async (teamId: number, thunkAPI) => {
    const response = await API.basketballTeams().getJudgeGames(teamId);
    return response?.data;
  }
);

export const fetchBasketballTeamJudgeGamesAutoAssignmentPreview =
  createAsyncThunk(
    'basketballTeam/getJudgeGamesAutoAssignmentPreview',
    async (withPaperAsst: boolean, thunkAPI) => {
      const response = await API.basketballMyTeam().getAllJudgeGameAllocations(
        withPaperAsst
      );
      return response?.data;
    }
  );

export const createBasketballTeamJudgeGamesAutoAssignment = createAsyncThunk(
  'basketballTeam/createBasketballTeamJudgeGamesAutoAssignment',
  async (
    data: RequestBasketballPostTeamJudgesUsersAutoAssignment,
    thunkAPI
  ) => {
    const response = await API.basketballMyTeam().allocateJudgeGames(data);
    return response?.data;
  }
);

export const fetchBasketballTeamPlayers = createAsyncThunk(
  'basketballTeam/getPlayers',
  async (_, thunkAPI) => {
    const userState = (thunkAPI.getState() as any).user as UserState;

    const teamId = userState.user?.basketball_user?.active_coach_team_id;
    if (teamId) {
      const response = await API.basketballTeams().getPlayers(teamId);
      return response?.data;
    } else {
      return [];
    }
  }
);

export const fetchBasketballTeamPlayersSelect = createAsyncThunk(
  'basketballTeam/getPlayersSelect',
  async (
    data: {
      teamId: number;
      cacheDuration: number;
    },
    thunkAPI
  ) => {
    if (!isCacheValid('basketballTeam/getPlayersSelect', data, thunkAPI)) {
      const response = await API.basketballTeams().getPlayersSelect(
        data.teamId
      );
      return response?.data;
    }
  }
);

/**
 *                  Team Task Actions
 */
export const fetchBasketballTeamTaskEvents = createAsyncThunk<any, number>(
  'basketballTeam/getTaskEvents',
  async (taskId: number, thunkAPI) => {
    const response = await API.basketballMyTeamTasks().getTaskEvents(taskId);
    if (response) {
      return thunkAPI.fulfillWithValue({
        taskId: taskId,
        data: response?.data,
      });
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/**
 *                  Messages Actions
 */
export const fetchBasketballTeamMessages = createAsyncThunk(
  'basketballTeam/getTeamMessages',
  async (page: number, thunkAPI) => {
    const response = await API.basketballMessages().getTeam(page);
    return response?.data;
  }
);

/**
 *                  Jerseys Actions
 */

/** @deprecated since version 1.0 */
export const updateBasketballTeamJerseys = createAsyncThunk(
  'basketballTeam/updateJerseys',
  async (data: { jerseys: number | undefined; id: number }, thunkAPI) => {
    const response = await API.basketballGames().saveJerseys(data);
    return response?.data;
  }
);

/**
 * Team Image Actions
 */
export const uploadTeamPhoto = createAsyncThunk(
  'basketballTeam/uploadTeamPhoto',
  async (
    data: {
      teamId: number;
      file: any;
      base64: boolean;
    },
    thunkAPI
  ) => {
    const response = data.base64
      ? await API.basketballTeams().uploadTeamPhotoAsBase64(
          data.teamId,
          data.file
        )
      : await API.basketballTeams().uploadTeamPhoto(data.teamId, data.file);
    return response?.data;
  }
);

export const deleteTeamPhoto = createAsyncThunk(
  'basketballTeam/deleteTeamPhoto',
  async (teamId: number, thunkAPI) => {
    const response = await API.basketballTeams().deleteTeamPhoto(teamId);
    return response?.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballTeamState {
  events: BasketballEvent[];
  games: any[]; // Deprecated - remove for 23/24 season
  judgeGames: BasketballTeamJudgeGame[];
  judgeGamesAutoAssignmentPreview: BasketballTeamJudgeGameAutoAssignment[];
  players: BasketballPlayer[];
  playersSelect: SelectOption[];
  tasks: Task[];
  taskEvents: { [key: number]: BasketballTeamTaskEvent[] };
  loadingMessages: boolean;
  messages: BasketballMessagesPagination;
}

const initialState: BasketballTeamState = {
  events: [],
  games: [], // Deprecated - remove for 23/24 season
  judgeGames: [],
  judgeGamesAutoAssignmentPreview: [],
  players: [],
  playersSelect: [],
  tasks: [],
  taskEvents: {},
  messages: {
    current_page: 0,
    data: [],
    from: 0,
    per_page: 0,
    to: 0,
  },
  loadingMessages: false,
};

export const basketballTeamSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchBasketballTeamEvents.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballEvent[]>
      ) => {
        state.events = action.payload;
      }
    );
    builder.addCase(
      fetchBasketballTeamGames.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<BasketballGame[]>) => {
        state.games = action.payload;
      }
    );
    builder.addCase(
      fetchBasketballTeamJudgeGames.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballTeamJudgeGame[]>
      ) => {
        state.judgeGames = action.payload;
      }
    );
    builder.addCase(
      fetchBasketballTeamJudgeGamesAutoAssignmentPreview.pending,
      (state: BasketballTeamState) => {
        state.judgeGamesAutoAssignmentPreview = [];
      }
    );
    builder.addCase(
      fetchBasketballTeamJudgeGamesAutoAssignmentPreview.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballTeamJudgeGameAutoAssignment[]>
      ) => {
        state.judgeGamesAutoAssignmentPreview = action.payload;
      }
    );
    builder.addCase(
      fetchBasketballTeamPlayers.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballPlayer[]>
      ) => {
        if (action.payload) {
          state.players = action.payload;
        }
      }
    );
    builder.addCase(
      updateBasketballPlayer.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<{ data: BasketballPlayer }>
      ) => {
        state.players = state.players?.map((player) =>
          player.id === action.payload.data.id ? action.payload.data : player
        );
      }
    );
    builder.addCase(
      fetchBasketballTeamPlayersSelect.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<SelectOption[]>) => {
        if (action.payload) {
          state.playersSelect = action.payload;
        }
      }
    );
    builder.addCase(
      createBasketballTeamJudgeGamesAutoAssignment.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballTeamJudgeGame[]>
      ) => {
        state.judgeGames = action.payload;
      }
    );
    builder.addCase(
      saveBasketballGame.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<BasketballGame>) => {
        const foundGame = state.events.find(
          (game) =>
            game.type === BasketballEventType.GAME &&
            game.id === action.payload.id
        );

        if (foundGame) {
          state.events = state.events.map((game) =>
            game.type === BasketballEventType.GAME &&
            game.id === action.payload.id
              ? action?.payload?.event ?? game
              : game
          );
        } else {
          if (action.payload.event) {
            state.events.push(action?.payload?.event);
          }
          state.events.sort(function (a, b) {
            return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
          });
        }
      }
    );
    builder.addCase(
      saveBasketballPractice.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<BasketballEvent>) => {
        const foundPractice = state.events.find(
          (practice) =>
            practice.type === BasketballEventType.PRACTICE &&
            practice.id === action.payload.id
        );
        if (foundPractice) {
          state.events = state.events.map((practice) =>
            practice.type === BasketballEventType.PRACTICE &&
            practice.id === action.payload.id
              ? action.payload
              : practice
          );
        } else {
          state.events.push(action.payload);
          state.events.sort(function (a, b) {
            return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
          });
        }
      }
    );
    builder.addCase(
      saveBasketballPractices.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballEvent[]>
      ) => {
        state.events = state.events.concat(action.payload);
        state.events = state.events.sort(function (a, b) {
          return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
        });
      }
    );
    builder.addCase(
      updateBasketballPractices.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballEvent[]>
      ) => {
        action.payload.forEach((practice) => {
          state.events = state.events.map((event) =>
            event.id === practice.id &&
            event.type === BasketballEventType.PRACTICE
              ? practice
              : event
          );
        });
        state.events = state.events.sort(function (a, b) {
          return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
        });
      }
    );
    builder.addCase(
      linkBasketballPractices.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballEvent[]>
      ) => {
        // Remove old practices
        state.events = state.events.filter(
          (e) => e.type !== BasketballEventType.PRACTICE
        );
        // add all new practices
        state.events = state.events.concat(action.payload);

        // Sort events by date
        state.events = state.events.sort(function (a, b) {
          return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
        });
      }
    );
    builder.addCase(
      deleteBasketballPractices.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<number[]>) => {
        state.events = state.events.filter(
          (event) =>
            (!action.payload.includes(event.id) &&
              event.type === BasketballEventType.PRACTICE) ||
            event.type === BasketballEventType.GAME
        );
      }
    );
    builder.addCase(
      deleteBasketballGame.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<number>) => {
        state.events = state.events.filter(
          (event) =>
            event.type === BasketballEventType.GAME &&
            event.id !== action.payload
        );
      }
    );
    builder.addCase(
      setBasketballGameJudgeUser.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<ResponseBasketballUpdateJudgeUser>
      ) => {
        const game = state.judgeGames.find(
          (g) => g.game_id === action.payload.game_id
        );

        if (game) {
          game.judge_paper = action.payload.judge_paper;
          game.judge_paper_asst = action.payload.judge_paper_asst;
          game.judge_time = action.payload.judge_time;
          game.judge_24sec = action.payload.judge_24sec;
        }
      }
    );
    /**
     *        Team Tasks
     */
    builder.addCase(
      fetchBasketballTeamTasks.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<Task[]>) => {
        if (action.payload) {
          state.tasks = action.payload;
        }
      }
    );
    builder.addCase(
      createBasketballTeamTask.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<Task>) => {
        if (action.payload) {
          state.tasks.push(action.payload);
          state.tasks = state.tasks.sort((a: Task, b: Task) =>
            a.name.localeCompare(b.name)
          );
        }
      }
    );
    builder.addCase(
      updateTask.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<Task>) => {
        if (action.payload) {
          state.tasks = state.tasks.map((task) =>
            task.id === action.payload.id ? action.payload : task
          );
        }
      }
    );
    builder.addCase(
      deleteTask.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<Task>) => {
        if (action.payload) {
          state.tasks = state.tasks.filter(
            (task) => task.id !== action.payload.id
          );
        }
      }
    );
    builder.addCase(
      fetchBasketballTeamTaskEvents.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<{
          taskId: number;
          data: BasketballTeamTaskEvent[];
        }>
      ) => {
        if (action.payload) {
          state.taskEvents[action.payload.taskId] = action.payload.data;
        }
      }
    );
    /******************************************************
     *               Messages Actions
     *****************************************************/
    builder.addCase(
      fetchBasketballTeamMessages.pending,
      (state: BasketballTeamState) => {
        state.loadingMessages = true;
      }
    );
    builder.addCase(
      fetchBasketballTeamMessages.rejected,
      (state: BasketballTeamState) => {
        state.loadingMessages = false;
      }
    );
    builder.addCase(
      fetchBasketballTeamMessages.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballMessagesPagination>
      ) => {
        if (action.payload.current_page === 1) {
          state.messages = action.payload;
        } else {
          state.messages = {
            ...action.payload,
            data: [...state.messages.data, ...action.payload.data],
          };
        }
        state.loadingMessages = false;
      }
    );
    builder.addCase(
      createBasketballMessage.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballMessage>
      ) => {
        if (!action.payload.adminMessage) {
          state.messages.data.unshift(action.payload);
        }
      }
    );
    builder.addCase(
      deleteBasketballMessage.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballMessage>
      ) => {
        state.messages.data = state.messages.data.filter(
          (message) => message.id !== action.payload.id
        );
      }
    );
    /******************************************************
     *               Jersey Actions
     *****************************************************/
    builder.addCase(
      updateBasketballTeamJerseys.fulfilled,
      (state: BasketballTeamState, action: PayloadAction<any>) => {
        state.games = state.games.map((game) => {
          if (game.game_id === action.payload.game_id) {
            return action.payload;
          } else {
            return game;
          }
        });
      }
    );
    /******************************************************
     *               Externe Actions
     *****************************************************/
    builder.addCase(
      assignUsersToBasketballEventTask.fulfilled,
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballTeamTask>
      ) => {
        if (action.payload) {
          const updatedGameId = action.payload.updated_game_id;
          const updatedPracticeId = action.payload.updated_practice_id;

          const eventId = updatedGameId
            ? '0_' + updatedGameId
            : '1_' + updatedPracticeId;
          const taskEvents = state.taskEvents[action.payload.id];
          if (taskEvents) {
            const event = taskEvents.find((ev) => ev.id === eventId);
            if (event) {
              const assignments:
                | BasketballTeamTaskEventAssignment[]
                | undefined = action.payload.assignments?.map(
                (assignment) =>
                  ({
                    id: assignment.id,
                    user_id: assignment.user_id,
                    user: assignment.user,
                  } as BasketballTeamTaskEventAssignment)
              );
              if (assignments) {
                event.assignments = assignments;
              }
            }
          }
        }
      }
    );
    builder.addMatcher(
      isAnyOf(
        addPlayersToBasketballTeam.fulfilled,
        removePlayersFromBasketballTeam.fulfilled
      ),
      (
        state: BasketballTeamState,
        action: PayloadAction<{
          isActiveCoachTeam: boolean;
          teamId: number;
          players: BasketballPlayer[];
        }>
      ) => {
        if (action.payload.isActiveCoachTeam) {
          state.players = action.payload.players;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(eventsApi.endpoints.saveBasketballEvent.matchFulfilled),
      (
        state: BasketballTeamState,
        action: PayloadAction<BasketballEventDetails>
      ) => {
        const meta = (action as any).meta;
        const originalArgs = meta.arg.originalArgs;
        const requestData = originalArgs.request as RequestBasketballPostEvent;

        const foundEvent = state.events.find(
          (event) => event.id === action.payload.id
        );

        const eventData: BasketballEvent = {
          date: action.payload.date,
          id: action.payload.id,
          type: BasketballEventType.EVENT,
          description: action.payload.name,
          end: action.payload.end_date ?? '',
          location: action.payload.location,
          status: action.payload.canceled
            ? BasketballEventStatus.CANCELED
            : BasketballEventStatus.SCHEDULED,
          teams: [],
          user_data: [],
          editable: true,
        };

        if (foundEvent) {
          state.events = state.events.map((event) =>
            event.id === action.payload.id ? eventData : event
          );
        } else {
          state.events.push(eventData);

          state.events = state.events.sort(function (a, b) {
            return dayjs(a.date).millisecond() - dayjs(b.date).millisecond();
          });
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballTeamReducer = basketballTeamSlice.reducer;
export const basketballTeamActions = basketballTeamSlice.actions;

const state = (rootState: any): BasketballTeamState => rootState[FEATURE_KEY];

export const basketballTeamEventsSelector = createSelector(state, (state) => {
  return state.events;
});

/** @deprecated since version 1.0 */
export const basketballTeamGamesSelector = createSelector(state, (state) => {
  return state.games;
});

export const basketballTeamJudgeGamesSelector = createSelector(
  state,
  (state) => {
    return state.judgeGames;
  }
);

export const basketballTeamJudgeGamesAutoAssignmentPreviewSelector =
  createSelector(state, (state) => {
    return state.judgeGamesAutoAssignmentPreview;
  });

export const basketballTeamPlayersSelector = createSelector(state, (state) => {
  return state.players;
});

export const basketballTeamPlayersSelectSelector = createSelector(
  state,
  (state) => {
    return state.playersSelect;
  }
);

export const basketballTeamPlayersSelectWithPrefixSelector = createSelector(
  state,
  (state) => {
    return state.playersSelect.map((player) => ({
      ...player,
      value: 'u_' + player.value,
    }));
  }
);

export const basketballTeamPlayersSelectWithPendingSelector = createSelector(
  state,
  (state) => {
    const options: SelectOption[] = [
      {
        value: null,
        label: t('pending'),
      },
    ];

    return [...options, ...state.playersSelect];
  }
);

export const basketballTeamTasksAllSelector = createSelector(state, (state) => {
  return state.tasks;
});

export const basketballTeamTasksTeamSelector = createSelector(
  state,
  (state) => {
    return state.tasks.filter(
      (task) => task.taskable_type === 'App\\Models\\Basketball\\BasketballTeam'
    );
  }
);

export const basketballTeamTasksClubSelector = createSelector(
  state,
  (state) => {
    return state.tasks.filter(
      (task) => task.taskable_type === 'App\\Models\\Base\\Club'
    );
  }
);

export const basketballTeamTaskEventsSelector = createSelector(
  state,
  (state) => {
    return state.taskEvents;
  }
);

export const basketballTeamMessagesLoadingSelector = createSelector(
  state,
  (state) => {
    return state.loadingMessages;
  }
);

export const basketballTeamMessagesSelector = createSelector(state, (state) => {
  return state.messages;
});
